import { useState } from "react";
import { useLang } from "../i18n/useLang";
import { useInstalarApp } from "../hooks/useInstalarApp";

/**
 * "Corvana en tu celular": instalar la app pensando en quien no se maneja con
 * la tecnología. Un botón grande cuando el navegador permite instalar con un
 * toque; si no (iPhone, o Android abierto desde WhatsApp), los pasos con
 * dibujos; y siempre la opción de mandarse el link por WhatsApp para abrirlo
 * en el celular.
 *
 *   modo="tarjeta"  para el inicio del portal: se oculta si ya está instalada
 *                   y se puede cerrar ("Ahora no").
 *   modo="pagina"   para /instalar y /portal/app: muestra todo siempre.
 */
// Con la barra final: es la carpeta build/instalar/ que tiene su propia vista
// previa para WhatsApp (ver scripts/vistas-previas.js), sin pasar por una
// redirección.
export const RUTA_INSTALAR = "/instalar/";

export function linkInstalar() {
  return `${window.location.origin}${RUTA_INSTALAR}`;
}

export function mensajeInstalar(L, condominio) {
  const donde = condominio ? L(` de ${condominio}`, ` for ${condominio}`) : "";
  return L(`Instalá la app de Corvana${donde} en tu celular. Abrí este link y seguí los pasos: `,
           `Install the Corvana app${donde} on your phone. Open this link and follow the steps: `) + linkInstalar();
}

const OCULTA_KEY = "instalar_tarjeta_oculta";

export default function InstalarCorvana({ modo = "tarjeta", condominio = "" }) {
  const L = useLang();
  const { prompt, instalada, plataforma, navegador, instalar } = useInstalarApp();
  const [oculta, setOculta] = useState(() => {
    try { return localStorage.getItem(OCULTA_KEY) === "1"; } catch { return false; }
  });
  const esTarjeta = modo === "tarjeta";

  if (esTarjeta && (instalada || oculta)) return null;

  function cerrar() {
    try { localStorage.setItem(OCULTA_KEY, "1"); } catch { /* modo privado */ }
    setOculta(true);
  }

  return (
    <div className={`ic-caja ${esTarjeta ? "tarjeta" : ""}`}>
      <style>{CSS}</style>
      <div className="ic-cabeza">
        <img src="/LOGO.png" alt="" className="ic-logo" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ic-titulo">{L("Corvana en tu celular", "Corvana on your phone")}</div>
          <div className="ic-sub">
            {L("Abrí tu portal con un toque, como cualquier app. No ocupa espacio y no hay que buscarla en la tienda.",
               "Open your portal with one tap, like any app. It takes no space and you don't need the app store.")}
          </div>
        </div>
        {esTarjeta && (
          <button type="button" className="ic-cerrar" onClick={cerrar} title={L("Ahora no", "Not now")}
            aria-label={L("Ahora no", "Not now")}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {instalada ? (
        <div className="ic-ok">
          <i className="fa-solid fa-circle-check" />
          {L("Ya tenés la app instalada en este aparato. Buscá el ícono de Corvana en tu pantalla.",
             "The app is already installed on this device. Look for the Corvana icon on your screen.")}
        </div>
      ) : prompt ? (
        <button type="button" className="ic-btn-instalar" onClick={instalar}>
          <i className="fa-solid fa-download" /> {L("Instalar la app", "Install the app")}
        </button>
      ) : plataforma === "ios" ? (
        <Pasos pasos={[
          { icono: "fa-brands fa-safari", texto: L("Abrí esta página en Safari (la brújula azul).", "Open this page in Safari (the blue compass).") },
          { icono: "fa-solid fa-arrow-up-from-bracket", texto: L("Tocá el botón Compartir, abajo en el centro.", "Tap the Share button at the bottom.") },
          { icono: "fa-regular fa-square-plus", texto: L("Bajá y tocá \"Agregar a inicio\".", "Scroll down and tap \"Add to Home Screen\".") },
          { icono: "fa-solid fa-check", texto: L("Tocá \"Agregar\". Listo: el ícono de Corvana queda en tu pantalla.", "Tap \"Add\". Done: the Corvana icon is on your screen.") },
        ]} />
      ) : plataforma === "android" ? (
        <Pasos pasos={[
          { icono: "fa-brands fa-chrome", texto: navegador === "samsung"
            ? L("Estás en Samsung Internet: tocá los 3 puntitos ⋮ de abajo a la derecha y abrí esta página en Chrome. Desde Chrome casi nunca se traba la instalación.",
                "You're in Samsung Internet: tap the 3 dots ⋮ at the bottom right and open this page in Chrome. From Chrome the install almost never gets stuck.")
            : L("Si abriste el link desde WhatsApp, tocá los 3 puntitos ⋮ y \"Abrir en Chrome\".",
                "If you opened the link from WhatsApp, tap the 3 dots ⋮ and \"Open in Chrome\".") },
          { icono: "fa-solid fa-ellipsis-vertical", texto: L("En Chrome, tocá los 3 puntitos ⋮ de arriba a la derecha.", "In Chrome, tap the 3 dots ⋮ at the top right.") },
          { icono: "fa-solid fa-mobile-screen-button", texto: L("Tocá \"Instalar aplicación\" o \"Agregar a pantalla principal\".", "Tap \"Install app\" or \"Add to Home screen\".") },
          { icono: "fa-solid fa-check", texto: L("Tocá \"Instalar\". Listo: el ícono de Corvana queda en tu pantalla.", "Tap \"Install\". Done: the Corvana icon is on your screen.") },
        ]} />
      ) : (
        <div className="ic-nota">
          <i className="fa-solid fa-computer" />
          {L("Estás en una computadora. Mandate el link por WhatsApp y abrilo en el celular: ahí te aparece el botón para instalarla.",
             "You're on a computer. Send yourself the link by WhatsApp and open it on your phone: you'll see the install button there.")}
        </div>
      )}

      {plataforma === "android" && !instalada && <AvisoPlayProtect L={L} samsung={navegador === "samsung"} />}

      <CompartirLinkApp condominio={condominio} />
    </div>
  );
}

/**
 * Google Play Protect corta la instalación en algunos celulares con "Se bloqueó
 * la app no segura - Esta app se diseñó para una versión anterior de Android".
 *
 * No es algo que se arregle desde Corvana: el APK no lo armamos nosotros, lo
 * arma el navegador en el momento a partir de la página, y Android lo revisa
 * como cualquier app bajada fuera de la tienda. Pasa sobre todo con Samsung
 * Internet. El aviso sí deja seguir, con "Instalar de todas formas", y desde
 * Chrome normalmente ni aparece. Va plegado para no asustar a quien no le salió.
 */
function AvisoPlayProtect({ L, samsung }) {
  return (
    <details className="ic-aviso">
      <summary>
        <i className="fa-solid fa-circle-question" />
        <span>{L("¿Te dijo que \"se bloqueó la app no segura\"?", "Did it say \"unsafe app blocked\"?")}</span>
        <i className="fa-solid fa-chevron-down ic-aviso-flecha" />
      </summary>
      <div className="ic-aviso-cuerpo">
        <p>
          {L("Es un aviso de Google Play Protect, no un problema de Corvana. Le sale a cualquier app que se instala desde el navegador en vez de la tienda. Podés seguir tranquilo:",
             "That's a Google Play Protect warning, not a problem with Corvana. It shows up for any app installed from the browser instead of the store. You can safely continue:")}
        </p>
        <ol className="ic-aviso-pasos">
          <li>{L("En el aviso, tocá \"Instalar de todas formas\" (el texto chiquito, arriba del botón azul).",
                 "In the warning, tap \"Install anyway\" (the small text above the blue button).")}</li>
          <li>{samsung
            ? L("Si no te aparece esa opción, abrí este mismo link en Chrome (3 puntitos ⋮ de abajo a la derecha) e instalá desde ahí.",
                "If you don't see that option, open this same link in Chrome (3 dots ⋮ at the bottom right) and install from there.")
            : L("Si no te aparece esa opción, abrí este mismo link en Chrome e instalá desde ahí.",
                "If you don't see that option, open this same link in Chrome and install from there.")}</li>
          <li>{L("Si igual no te deja, no pasa nada: entrá a Corvana desde el navegador como siempre, con el mismo usuario.",
                 "If it still won't let you, no problem: open Corvana in the browser as usual, with the same user.")}</li>
        </ol>
      </div>
    </details>
  );
}

function Pasos({ pasos }) {
  return (
    <ol className="ic-pasos">
      {pasos.map((p, i) => (
        <li key={i}>
          <span className="ic-num">{i + 1}</span>
          <i className={`${p.icono} ic-paso-icono`} />
          <span>{p.texto}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Mandar el link de instalación: WhatsApp, copiar o el menú de compartir del
 * celular. Lo usan el residente (para su familia o para su propio celular) y
 * la administración (para mandárselo a los residentes).
 */
export function CompartirLinkApp({ condominio = "", titulo }) {
  const L = useLang();
  const [copiado, setCopiado] = useState(false);
  const link = linkInstalar();
  const mensaje = mensajeInstalar(L, condominio);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Navegadores viejos o sin permiso de portapapeles.
      const t = document.createElement("textarea");
      t.value = link;
      document.body.appendChild(t);
      t.select();
      try { document.execCommand("copy"); } catch { /* sin portapapeles */ }
      t.remove();
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2200);
  }

  function compartir() {
    navigator.share({ title: "Corvana", text: mensaje.replace(link, "").trim(), url: link }).catch(() => {});
  }

  return (
    <div className="ic-compartir">
      <style>{CSS}</style>
      <div className="ic-compartir-titulo">
        {titulo || L("Mandar el link (a tu celular o a tu familia)", "Send the link (to your phone or your family)")}
      </div>
      <div className="ic-link">
        <i className="fa-solid fa-link" />
        <span>{link}</span>
      </div>
      <div className="ic-botones">
        <a className="ic-btn ic-btn-wa" href={`https://wa.me/?text=${encodeURIComponent(mensaje)}`}
          target="_blank" rel="noreferrer">
          <i className="fa-brands fa-whatsapp" /> WhatsApp
        </a>
        <button type="button" className="ic-btn" onClick={copiar}>
          <i className={`fa-solid ${copiado ? "fa-check" : "fa-copy"}`} /> {copiado ? L("Copiado", "Copied") : L("Copiar link", "Copy link")}
        </button>
        {typeof navigator !== "undefined" && navigator.share && (
          <button type="button" className="ic-btn" onClick={compartir}>
            <i className="fa-solid fa-share-nodes" /> {L("Compartir", "Share")}
          </button>
        )}
      </div>
    </div>
  );
}

const CSS = `
.ic-caja { background: var(--bg-card); border: 1px solid rgba(var(--brand-rgb), .35); border-radius: 16px;
  padding: 18px; display: flex; flex-direction: column; gap: 14px; box-shadow: 0 2px 12px var(--shadow); }
.ic-caja i, .ic-caja button *, .ic-caja a *, .ic-compartir i, .ic-compartir button *, .ic-compartir a * { color: inherit; }
.ic-caja.tarjeta { margin-bottom: 20px;
  background: linear-gradient(135deg, rgba(var(--brand-rgb), .10), rgba(var(--brand-rgb), .02)), var(--bg-card); }
.ic-cabeza { display: flex; gap: 14px; align-items: flex-start; }
.ic-logo { width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0; object-fit: cover;
  box-shadow: 0 4px 14px rgba(var(--brand-rgb), .3); }
.ic-titulo { font-size: 17px; font-weight: 800; color: var(--text-primary); }
.ic-sub { font-size: 13.5px; color: var(--text-secondary); margin-top: 3px; line-height: 1.45; }
.ic-cerrar { width: 34px; height: 34px; border-radius: 9px; border: none; background: transparent; flex-shrink: 0;
  color: var(--text-secondary); cursor: pointer; }
.ic-cerrar:hover { background: var(--hover-bg-strong); color: var(--text-primary); }
.ic-btn-instalar { min-height: 56px; border: none; border-radius: 14px; cursor: pointer; width: 100%;
  background: var(--brand); color: #fff; font-size: 17px; font-weight: 800;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  box-shadow: 0 6px 20px rgba(var(--brand-rgb), .4); }
.ic-btn-instalar:hover { background: var(--brand-deep); }
.ic-ok, .ic-nota { display: flex; gap: 10px; align-items: flex-start; padding: 12px 14px; border-radius: 12px;
  font-size: 14px; line-height: 1.45; }
.ic-ok { background: rgba(16,185,129,.12); border: 1px solid rgba(16,185,129,.4); color: #6ee7b7; }
[data-theme="light"] .ic-ok { color: #047857; }
.ic-nota { background: var(--hover-bg); border: 1px solid var(--border-card); color: var(--text-primary); }
.ic-ok i, .ic-nota i { margin-top: 3px; }
.ic-pasos { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.ic-pasos li { display: flex; gap: 12px; align-items: center; padding: 10px 12px; border-radius: 12px;
  background: var(--hover-bg); border: 1px solid var(--border-card); font-size: 14.5px; color: var(--text-primary);
  line-height: 1.4; }
.ic-num { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; background: var(--brand); color: #fff;
  font-size: 13px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
.ic-paso-icono { width: 22px; text-align: center; font-size: 18px; flex-shrink: 0; color: var(--brand) !important; }
.ic-aviso { border: 1px solid var(--border-card); border-radius: 12px; background: var(--hover-bg); }
.ic-aviso summary { list-style: none; cursor: pointer; display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; font-size: 14px; font-weight: 700; color: var(--text-primary); }
.ic-aviso summary::-webkit-details-marker { display: none; }
.ic-aviso summary > span { flex: 1; min-width: 0; }
.ic-aviso summary > i:first-child { color: var(--brand) !important; font-size: 16px; }
.ic-aviso-flecha { font-size: 12px; color: var(--text-secondary) !important; transition: transform .18s; }
.ic-aviso[open] .ic-aviso-flecha { transform: rotate(180deg); }
.ic-aviso-cuerpo { padding: 0 14px 14px; font-size: 13.5px; line-height: 1.5; color: var(--text-secondary); }
.ic-aviso-cuerpo p { margin: 0 0 10px; }
.ic-aviso-pasos { margin: 0; padding-left: 20px; display: flex; flex-direction: column; gap: 7px; }
.ic-compartir { display: flex; flex-direction: column; gap: 8px; }
.ic-compartir-titulo { font-size: 12px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase;
  color: var(--text-secondary); }
.ic-link { display: flex; gap: 8px; align-items: center; padding: 9px 12px; border-radius: 10px; font-size: 14px;
  background: var(--bg-input); border: 1px solid var(--border-input); color: var(--text-primary); min-width: 0; }
.ic-link span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.ic-botones { display: flex; gap: 8px; flex-wrap: wrap; }
.ic-btn { flex: 1 1 120px; min-height: 44px; padding: 8px 14px; border-radius: 12px; cursor: pointer;
  border: 1px solid var(--border-card); background: var(--bg-input); color: var(--text-primary);
  font-size: 14px; font-weight: 700; text-decoration: none;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
.ic-btn:hover { border-color: rgba(var(--brand-rgb), .5); color: var(--text-primary); }
.ic-btn-wa { background: #128c4a; border-color: #128c4a; color: #fff; }
.ic-btn-wa:hover { background: #0f7a40; color: #fff; }
`;
